/**
 * A worker's pay basis — `employee_pay_profiles.pay_basis` (api#1097, 0523).
 *
 * Mirrors the CHECK constraint and `PAY_BASES` in the API's
 * pay-profiles.routes.ts: 'hourly' | 'percentage' | 'day_rate' | 'salary' |
 * 'bol_percent'. 'percentage' means "paid from tickets", the way every casing
 * hand has always been paid; it did not exist before 0523 because a worker
 * with NO profile was paid that way implicitly.
 *
 * ── CANADA LOOKS EXACTLY AS IT DID; THE US GETS HOURLY ──────────────────────
 * Operator decision, 2026-10-16: a Canadian tenant's profile screens are
 * unchanged from before api#1097 — same options, same default — and only a US
 * tenant sees the new behaviour. The API routes clock pay for US tenants only
 * (hourly-pay.service.ts), so a Canadian profile's basis changes nothing about
 * how that hand is paid; there was no reason to move their screens.
 *
 *   /payroll/workers (WorkerPayProfileScreen) — the casing tenants' screen
 *     CA / unknown: Day Rate · Hourly · Salary, default Day Rate   (as prod)
 *     US:           Percentage · Hourly · Day Rate · Salary, default Hourly
 *
 *   EmployeeProfile's pay dialog — mounted for trucking tenants only
 *     CA / unknown: Hourly · BOL Percentage · Salary · Day Rate     (as prod)
 *     US:           all five
 *     default Hourly everywhere                                    (as prod)
 *
 * "Unknown" (no company in reach yet) is treated as not-US: it gets the prod
 * screen, which is the screen every tenant had yesterday, rather than a guess
 * that the tenant is American.
 *
 * ── A STORED BASIS IS ALWAYS SHOWN ──────────────────────────────────────────
 * An existing profile keeps its basis, and a control that has no option for
 * the stored value shows nothing selected while the save still posts it. So
 * each option list is the country's list PLUS the stored basis when it is not
 * already there — a CA worker saved as 'percentage' still sees Percentage.
 */

export const PAY_BASES = ['hourly', 'percentage', 'day_rate', 'salary', 'bol_percent'] as const;
export type PayBasis = (typeof PAY_BASES)[number];

export const PAY_BASIS_LABELS: Record<PayBasis, string> = {
  hourly: 'Hourly',
  percentage: 'Percentage',
  day_rate: 'Day Rate',
  salary: 'Salary',
  bol_percent: 'BOL Percentage',
};

export function isPayBasis(value: unknown): value is PayBasis {
  return typeof value === 'string' && (PAY_BASES as readonly string[]).includes(value);
}

const isUS = (country: unknown) => country === 'US';

/** The country's list, plus a stored basis it does not already contain. */
function withStored(list: readonly PayBasis[], stored: unknown): PayBasis[] {
  return isPayBasis(stored) && !list.includes(stored) ? [...list, stored] : [...list];
}

/** /payroll/workers — the options, in display order. */
export function workerScreenBasisOptions(country: unknown, stored?: unknown): PayBasis[] {
  return withStored(
    isUS(country) ? ['percentage', 'hourly', 'day_rate', 'salary'] : ['day_rate', 'hourly', 'salary'],
    stored,
  );
}

/** /payroll/workers — what a NEW profile starts at. */
export function workerScreenDefaultBasis(country: unknown): PayBasis {
  return isUS(country) ? 'hourly' : 'day_rate';
}

/** EmployeeProfile's pay dialog — the options, in display order. */
export function dialogBasisOptions(country: unknown, stored?: unknown): PayBasis[] {
  return withStored(
    isUS(country) ? ['hourly', 'percentage', 'bol_percent', 'salary', 'day_rate'] : ['hourly', 'bol_percent', 'salary', 'day_rate'],
    stored,
  );
}

/** EmployeeProfile's pay dialog — what a NEW profile starts at, as on prod. */
export const DIALOG_DEFAULT_BASIS: PayBasis = 'hourly';

/**
 * The label for a stored basis. A value this build does not know is shown as
 * an em dash rather than echoed raw or guessed (label maps are exhaustive).
 */
export function payBasisLabel(value: unknown): string {
  return isPayBasis(value) ? PAY_BASIS_LABELS[value] : '—';
}

/**
 * A percentage worker is paid from tickets at the percentages on their pay
 * rates, so the profile's base rate prices nothing. The column is NOT NULL, and
 * the API's contract is to send 0 for it (api-b0, 2026-10-16). That 0 is never
 * displayed as a rate — see `formatBaseRate` in EmployeeProfile.tsx.
 */
export const PERCENTAGE_BASE_RATE = 0;
